import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function AdminLogin() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [busy, setBusy] = useState(false);

  const mfaRequired = Boolean(challengeToken);

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);

    try {
      const data = await login(email, password);

      if (data.mfa_required) {
        setChallengeToken(data.challenge_token);
        setPassword("");
        setMfaCode("");
        return;
      }

      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Login failed."
      );
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setBusy(true);

    try {
      const data = await verifyMfa(challengeToken, mfaCode);

      if (data.recovery_code_used) {
        toast.warning(
          "Recovery code used. That code cannot be used again."
        );
      } else {
        toast.success("Authentication successful.");
      }

      setChallengeToken("");
      setMfaCode("");
      navigate("/admin");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Authentication failed."
      );
    } finally {
      setBusy(false);
    }
  };

  const resetLogin = () => {
    setChallengeToken("");
    setMfaCode("");
    setPassword("");
  };

  const inputCls =
    "w-full rounded-sm border border-white/15 bg-white/5 px-4 py-3 text-white text-sm outline-none focus:border-cyan transition-colors placeholder:text-white/30";

  return (
    <div
      className="min-h-screen grid place-items-center bg-navy grain-overlay relative px-5"
      data-testid="page-admin-login"
    >
      <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-brandblue/20 blur-[120px] rounded-full" />

      <form
        onSubmit={mfaRequired ? submitMfa : submitPassword}
        className="relative z-10 w-full max-w-sm bg-white/[0.03] border border-white/10 backdrop-blur-xl rounded-md p-8"
      >
        <div className="flex items-center gap-3 mb-8">
          <Logo variant="white" />
          <span className="text-[10px] tracking-[0.2em] text-cyan font-semibold border-l border-white/20 pl-3">
            ADMIN CONSOLE
          </span>
        </div>

        {!mfaRequired ? (
          <>
            <h1 className="text-white font-heading font-bold text-2xl flex items-center gap-2">
              <Lock size={20} className="text-cyan" />
              Sign in
            </h1>

            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold tracking-wide text-white/60 mb-2 uppercase">
                  Email
                </label>
                <input
                  className={inputCls}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-email"
                  placeholder="admin@tworay.co.uk"
                  autoComplete="username"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold tracking-wide text-white/60 mb-2 uppercase">
                  Password
                </label>
                <input
                  className={inputCls}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-white font-heading font-bold text-2xl flex items-center gap-2">
              <ShieldCheck size={21} className="text-cyan" />
              Verify your identity
            </h1>

            <p className="mt-3 text-sm leading-relaxed text-white/60">
              Enter the 6-digit code from your authenticator app. You can also
              enter one of your recovery codes.
            </p>

            <div className="mt-6">
              <label className="block text-xs font-semibold tracking-wide text-white/60 mb-2 uppercase">
                Authentication or recovery code
              </label>

              <input
                className={inputCls}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                data-testid="admin-mfa-code"
                placeholder="123456"
                autoComplete="one-time-code"
                autoFocus
                required
              />
            </div>
          </>
        )}

        <button
          disabled={busy}
          data-testid={mfaRequired ? "admin-mfa-submit" : "admin-login-btn"}
          className="mt-7 w-full bg-cyan text-navy font-semibold text-sm px-6 py-3 rounded-sm hover:bg-white transition-colors disabled:opacity-50"
        >
          {busy
            ? "Checking…"
            : mfaRequired
              ? "Verify and sign in"
              : "Sign in"}
        </button>

        {mfaRequired && (
          <button
            type="button"
            onClick={resetLogin}
            disabled={busy}
            className="mt-4 w-full text-center text-xs text-white/50 hover:text-white transition-colors"
          >
            Back to password sign in
          </button>
        )}
      </form>
    </div>
  );
}
